// @flow
import * as React from 'react';
import PropTypes from 'prop-types';

import type {Collaborator, Store} from '../../../../entities';
import {
  Divider,
  Grid,
  IconButton,
  makeStyles,
  Snackbar,
  Typography,
} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';

import {colors} from '../../../../assets/styles/colors';
import {useEffect} from 'react';
import Alert from '@material-ui/lab/Alert';
import {Link, Route, Switch, useHistory} from 'react-router-dom';
import {AddCircleTwoTone, ArrowBack, Edit} from '@material-ui/icons';
import {isFeatureInTier, useIsFeatureInTier} from '../../../../utils/component';
import AccountsCollaboratorEditContainer from '../containers/AccountsCollaboratorEditContainer';

type Props = {|
  store?: Store,
  fetchCollaborators: ({collaboratorsRequest: Object}) => void,
  collaborators?: Collaborator[],
  saveSuccess?: boolean,
  resetState: () => void,
|};

export const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: '12px',
    padding: theme.spacing(2),
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingBottom: theme.spacing(2),
  },
  subRouteHeader: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  subRouteTitle: {
    marginLeft: theme.spacing(1),
    fontSize: '1rem',
    fontWeight: 600,
  },
  collaboratorsNumber: {
    marginLeft: 'auto',
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  divider: {
    background: colors.grey200,
    width: '100%',
  },
  collaboratorButton: {
    // margin: theme.spacing(2, 2, 0, 0),
    borderRadius: '12px',
    border: `1px solid ${colors.grey300}`,
    padding: theme.spacing(2, 2, 2, 2),
    width: '100%',
    height: '100%',
  },
  collaboratorButtonContent: {
    position: 'relative',
    width: '100%',
    height: '100%',
    // display: 'flex',
    // flexDirection: 'column',
    // alignItems: 'flex-start',
  },
  collaboratorName: {
    color: colors.grey900,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  collaboratorInfo: {
    color: colors.grey700,
    maxWidth: 'calc(100% - 40px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  collaboratorActive: {},
  mobileHDivider: {
    position: 'relative',
    left: '-16px',
    marginTop: theme.spacing(1),
    backgroundColor: colors.grey300,
    width: 'calc(100% + 32px)',
  },
  bottomButton: {
    // margin: theme.spacing(1),
  },
  addCollaboratorButton: {
    border: '1px dashed #B8B8C7',
  },
  addCollaboratorButtonContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    // marginBottom: 16,
  },
  addCollaboratorButtonText: {
    color: colors.grey700,
    fontWeight: 500,
    fontSize: '0.875rem',
  },

  checkboxLabel: {
    color: colors.grey700,
    fontSize: '0.875rem',
    margin: theme.spacing(3, 0),
  },
  select: {
    borderRadius: '8px',
  },
  saveButton: {
    alignSelf: 'center',
    marginTop: theme.spacing(3),
    width: 300,
    height: 48,
    fontSize: '1.125rem',
    [theme.breakpoints.down('xs')]: {
      zIndex: 1,
      position: 'fixed',
      left: 0,
      bottom: 64,
      margin: theme.spacing(2),
      width: `calc(100% - ${theme.spacing(4)})`,
    },
  },
  saveAlert: {
    // width: '100%',
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
    },
  },
  infoAlert: {
    // width: '100%',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(2),
    },
  },
}));

export default function Collaborators(props: Props) {
  const {
    store,
    fetchCollaborators,
    collaborators,
    saveSuccess,
    resetState,
  } = props;

  const classes = useStyles();
  const history = useHistory();

  const hasCollaboratorsFeature = useIsFeatureInTier('STORE_COLLABORATOR');

  const collaboratorsLimitFeature =
    !!store && isFeatureInTier('STORE_COLLABORATOR', store);
  const collaboratorsLimit =
    collaboratorsLimitFeature &&
    collaboratorsLimitFeature.spec &&
    collaboratorsLimitFeature.spec.limit;

  useEffect(() => {
    if (!hasCollaboratorsFeature) history.replace('/configure/accounts');
  }, [collaborators, hasCollaboratorsFeature, history]);

  useEffect(() => {
    if (hasCollaboratorsFeature && !collaborators)
      fetchCollaborators({collaboratorsRequest: {archived: false}});
  }, [collaborators, fetchCollaborators, hasCollaboratorsFeature]);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  // const [credentialsCopied, setCredentialsCopied] = useState(false);

  // const handleClickCopyCredentials = (collaborator: Collaborator) => {
  //   const credentials = `Correo: ${collaborator.email}`;
  //   navigator &&
  //     navigator.clipboard &&
  //     navigator.clipboard
  //       .writeText(credentials)
  //       .then(() => setCredentialsCopied(true))
  //       .catch(e => console.error(e));
  // };

  return (
    <Switch>
      <Route exact path="/configure/accounts/collaborators">
        <div className={classes.root}>
          <Snackbar open={saveSuccess} autoHideDuration={4000}>
            <Alert variant="filled" severity="success">
              Colaborador guardado con éxito
            </Alert>
          </Snackbar>
          <div className={classes.subRouteHeader}>
            <IconButton
              variant="contained"
              size="small"
              component={Link}
              to="/configure/accounts"
            >
              <ArrowBack />
            </IconButton>
            <Typography className={classes.subRouteTitle}>
              Colaboradores
            </Typography>
            {collaborators && collaboratorsLimitFeature && (
              <Typography className={classes.collaboratorsNumber}>
                {collaborators.length}/{collaboratorsLimit}
              </Typography>
            )}
          </div>
          <Divider className={classes.divider} />
          <Grid container style={{marginTop: 16}} spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <CustomButton
                className={`${classes.collaboratorButton} ${classes.addCollaboratorButton}`}
                component={Link}
                to={`/configure/accounts/collaborators/new`}
                onClick={resetState}
                disabled={
                  collaborators &&
                  collaboratorsLimit &&
                  collaboratorsLimit <= collaborators.length
                }
              >
                <div className={classes.addCollaboratorButtonContent}>
                  <AddCircleTwoTone
                    // style={{color: colors.grey700}}
                    fontSize="large"
                  />
                  <Typography className={classes.addCollaboratorButtonText}>
                    Agregar colaborador
                  </Typography>
                </div>
              </CustomButton>
            </Grid>
            {collaborators &&
              collaborators.map(collaborator => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  lg={3}
                  key={`collaborator-${collaborator.id}`}
                >
                  <CustomButton
                    className={`${classes.collaboratorButton}`}
                    component={Link}
                    to={`/configure/accounts/collaborators/${collaborator.id}`}
                    onClick={resetState}
                  >
                    <div className={classes.collaboratorButtonContent}>
                      <div style={{position: 'relative'}}>
                        <Typography className={classes.collaboratorName}>
                          {`${collaborator.firstName} ${collaborator.lastName}`}
                        </Typography>
                        <Typography className={classes.collaboratorInfo}>
                          {collaborator.email}
                        </Typography>
                        <Typography className={classes.collaboratorInfo}>
                          {collaborator.phone}
                        </Typography>
                        <div className={classes.collaboratorActive}></div>
                        <div
                          style={{
                            position: 'absolute',
                            padding: '8px 8px 0px 8px',
                            right: 0,
                            bottom: 0,
                            color: colors.grey600,
                            backgroundColor: colors.grey200,
                            borderRadius: 40,
                          }}
                        >
                          <Edit />
                        </div>
                      </div>
                      {/* <Divider className={classes.mobileHDivider} />
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-around',
                    }}
                  >
                    <CustomButton
                      className={classes.bottomButton}
                      startIcon={<FileCopy />}
                      onClick={() => handleClickCopyCredentials(collaborator)}
                    >
                      Copiar accesos
                    </CustomButton>
                  </div> */}
                    </div>
                  </CustomButton>
                </Grid>
              ))}
          </Grid>
        </div>
      </Route>
      <Route
        path="/configure/accounts/collaborators/:collaboratorId"
        component={AccountsCollaboratorEditContainer}
      />
    </Switch>
  );
}

Collaborators.propTypes = {
  store: PropTypes.object,
  fetchCollaborators: PropTypes.func.isRequired,
  collaborators: PropTypes.array,
  saveSuccess: PropTypes.bool,
  resetState: PropTypes.func.isRequired,
};
